// src/pages/AdminManagement.js
import React, { useState, useEffect } from 'react';
import { collection, onSnapshot, doc, getDoc } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { db } from '../firebase';

const FUNCTIONS_BASE = 'https://us-central1-crewboard-92511.cloudfunctions.net';

async function callFunction(name, body) {
  const auth = getAuth();
  const token = await auth.currentUser?.getIdToken();
  const res = await fetch(`${FUNCTIONS_BASE}/${name}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Request failed');
  return data;
}

const s = {
  page: { color: 'var(--text)' },
  header: { marginBottom: 32 },
  title: { fontSize: 26, fontWeight: 700, color: 'var(--text)', margin: 0 },
  subtitle: { color: 'var(--text-muted)', fontSize: 14, marginTop: 6 },

  grid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, marginBottom: 32 },

  card: { background: 'var(--bg)', border: '1px solid var(--surface2)', borderRadius: 14, padding: 24 },
  cardTitle: { fontSize: 16, fontWeight: 600, color: 'var(--text)', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 8 },

  label: { display: 'block', color: 'var(--text-secondary)', fontSize: 12, marginBottom: 6, marginTop: 16 },
  input: { width: '100%', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 8, padding: '10px 14px', color: 'var(--text)', fontSize: 14, boxSizing: 'border-box', outline: 'none' },
  select: { width: '100%', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 8, padding: '10px 14px', color: 'var(--text)', fontSize: 14, boxSizing: 'border-box', outline: 'none' },

  btnPrimary: { width: '100%', marginTop: 20, background: '#F97316', border: 'none', borderRadius: 8, padding: '12px 20px', color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  btnDisabled: { width: '100%', marginTop: 20, background: 'var(--border)', border: 'none', borderRadius: 8, padding: '12px 20px', color: 'var(--text-muted)', fontSize: 14, fontWeight: 600, cursor: 'not-allowed' },

  successBox: { background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.2)', borderRadius: 8, padding: '12px 16px', color: '#22C55E', fontSize: 13, marginTop: 16 },
  errorBox: { background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 8, padding: '12px 16px', color: '#EF4444', fontSize: 13, marginTop: 16 },

  table: { width: '100%', borderCollapse: 'collapse' },
  th: { textAlign: 'left', color: 'var(--text-muted)', fontSize: 12, fontWeight: 500, padding: '8px 12px', borderBottom: '1px solid var(--surface2)' },
  td: { padding: '14px 12px', borderBottom: '1px solid #111', fontSize: 14, color: 'var(--text-secondary)', verticalAlign: 'middle' },

  roleBadge: (role) => ({
    display: 'inline-block',
    padding: '3px 10px',
    borderRadius: 20,
    fontSize: 11,
    fontWeight: 600,
    background: role === 'superadmin' ? 'rgba(249,115,22,0.12)' : 'rgba(99,102,241,0.12)',
    color: role === 'superadmin' ? '#F97316' : '#818CF8',
    border: role === 'superadmin' ? '1px solid rgba(249,115,22,0.2)' : '1px solid rgba(99,102,241,0.2)',
  }),

  deleteBtn: { background: 'transparent', border: '1px solid var(--border)', borderRadius: 6, padding: '5px 12px', color: '#EF4444', fontSize: 12, cursor: 'pointer' },
  selfBadge: { background: 'var(--surface2)', borderRadius: 6, padding: '5px 12px', color: 'var(--text-muted)', fontSize: 12 },

  infoBox: { background: 'rgba(249,115,22,0.06)', border: '1px solid rgba(249,115,22,0.15)', borderRadius: 10, padding: '14px 18px', marginBottom: 24, fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.7 },

  emptyState: { textAlign: 'center', padding: '40px 20px', color: 'var(--text-muted)', fontSize: 14 },
};

export default function AdminManagement() {
  const auth = getAuth();
  const currentUser = auth.currentUser;

  const [admins, setAdmins] = useState([]);
  const [currentAdminRole, setCurrentAdminRole] = useState('admin');
  const [loadingAdmins, setLoadingAdmins] = useState(true);

  const [form, setForm] = useState({ email: '', password: '', fullName: '', role: 'admin' });
  const [creating, setCreating] = useState(false);
  const [createResult, setCreateResult] = useState(null); // {type: 'success'|'error', message}

  const [deletingUid, setDeletingUid] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteResult, setDeleteResult] = useState(null);

  // Load current admin's role
  useEffect(() => {
    if (!currentUser) return;
    getDoc(doc(db, 'admins', currentUser.uid)).then(snap => {
      if (snap.exists()) setCurrentAdminRole(snap.data().role || 'admin');
    });
  }, [currentUser]);

  // Live list of all admins
  useEffect(() => {
    const unsub = onSnapshot(collection(db, 'admins'), (snap) => {
      const list = snap.docs.map(d => ({ uid: d.id, ...d.data() }));
      list.sort((a, b) => {
        if (a.role === 'superadmin' && b.role !== 'superadmin') return -1;
        if (b.role === 'superadmin' && a.role !== 'superadmin') return 1;
        return (a.email || '').localeCompare(b.email || '');
      });
      setAdmins(list);
      setLoadingAdmins(false);
    });
    return unsub;
  }, []);

  const isSuperAdmin = currentAdminRole === 'superadmin';

  const handleCreate = async () => {
    setCreateResult(null);
    if (!form.email || !form.password) {
      setCreateResult({ type: 'error', message: 'Email and password are required.' });
      return;
    }
    if (form.password.length < 8) {
      setCreateResult({ type: 'error', message: 'Password must be at least 8 characters.' });
      return;
    }
    setCreating(true);
    try {
      const result = await callFunction('createAdminAccount', {
        email: form.email.trim().toLowerCase(),
        password: form.password,
        fullName: form.fullName.trim(),
        role: form.role,
      });
      setCreateResult({ type: 'success', message: `✅ Admin account created for ${form.email}. A welcome email has been sent.` });
      setForm({ email: '', password: '', fullName: '', role: 'admin' });
    } catch (err) {
      setCreateResult({ type: 'error', message: err.message || 'Failed to create admin account.' });
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteClick = (admin) => {
    setDeleteTarget(admin);
    setDeleteConfirm('');
    setDeleteResult(null);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    if (deleteConfirm !== deleteTarget.email) {
      setDeleteResult({ type: 'error', message: 'Email does not match. Please type the exact email to confirm.' });
      return;
    }
    setDeletingUid(deleteTarget.uid);
    setDeleteResult(null);
    try {
      await callFunction('deleteAdminAccount', { uid: deleteTarget.uid });
      setDeleteResult({ type: 'success', message: `✅ Admin account deleted.` });
      setDeleteTarget(null);
      setDeleteConfirm('');
    } catch (err) {
      setDeleteResult({ type: 'error', message: err.message || 'Failed to delete admin account.' });
    } finally {
      setDeletingUid(null);
    }
  };

  return (
    <div style={s.page}>
      <div style={s.header}>
        <h1 style={s.title}>🔐 Admin Management</h1>
        <p style={s.subtitle}>Create and manage admin accounts. Only superadmins can add or remove admins.</p>
      </div>

      {!isSuperAdmin && (
        <div style={s.infoBox}>
          👁️ <strong style={{ color: '#F97316' }}>View only.</strong> You have admin access but only superadmins can create or delete admin accounts. Contact a superadmin to make changes.
        </div>
      )}

      <div style={s.grid}>
        {/* Create Admin Form */}
        <div style={s.card}>
          <div style={s.cardTitle}>
            <span>➕</span> Add New Admin
          </div>

          {!isSuperAdmin ? (
            <div style={{ color: 'var(--text-muted)', fontSize: 13, textAlign: 'center', padding: '20px 0' }}>
              Superadmin access required to create accounts.
            </div>
          ) : (
            <>
              <label style={s.label}>Full Name</label>
              <input
                style={s.input}
                placeholder="Jane Smith"
                value={form.fullName}
                onChange={e => setForm(p => ({ ...p, fullName: e.target.value }))}
              />

              <label style={s.label}>Email Address *</label>
              <input
                style={s.input}
                type="email"
                placeholder="admin@crewboard.ca"
                value={form.email}
                onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
              />

              <label style={s.label}>Password * (min 8 characters)</label>
              <input
                style={s.input}
                type="password"
                placeholder="Strong password"
                value={form.password}
                onChange={e => setForm(p => ({ ...p, password: e.target.value }))}
              />

              <label style={s.label}>Role</label>
              <select
                style={s.select}
                value={form.role}
                onChange={e => setForm(p => ({ ...p, role: e.target.value }))}
              >
                <option value="admin">Admin — can manage users & verifications</option>
                <option value="superadmin">Superadmin — full access + can manage admins</option>
              </select>

              <button
                style={creating ? s.btnDisabled : s.btnPrimary}
                onClick={handleCreate}
                disabled={creating}
              >
                {creating ? 'Creating Account...' : '➕ Create Admin Account'}
              </button>

              {createResult && (
                <div style={createResult.type === 'success' ? s.successBox : s.errorBox}>
                  {createResult.message}
                </div>
              )}
            </>
          )}
        </div>

        {/* Role Info Card */}
        <div style={s.card}>
          <div style={s.cardTitle}><span>ℹ️</span> Role Permissions</div>
          <div style={{ fontSize: 13, lineHeight: 1.9 }}>
            <div style={{ marginBottom: 16 }}>
              <div style={{ ...s.roleBadge('superadmin'), marginBottom: 8 }}>superadmin</div>
              <ul style={{ color: 'var(--text-secondary)', margin: '6px 0', paddingLeft: 18 }}>
                <li>Everything admins can do</li>
                <li>Create & delete admin accounts</li>
                <li>Access Admin Management page</li>
                <li>Cannot be deleted by other admins</li>
              </ul>
            </div>
            <div style={{ borderTop: '1px solid var(--surface2)', paddingTop: 16 }}>
              <div style={{ ...s.roleBadge('admin'), marginBottom: 8 }}>admin</div>
              <ul style={{ color: 'var(--text-secondary)', margin: '6px 0', paddingLeft: 18 }}>
                <li>Verify/reject CDL licenses</li>
                <li>Suspend & reactivate users</li>
                <li>Handle data deletion requests</li>
                <li>Send notifications</li>
                <li>View subscriptions</li>
              </ul>
            </div>
          </div>

          <div style={{ marginTop: 20, padding: '12px 14px', background: 'var(--bg)', borderRadius: 8, border: '1px solid var(--surface2)' }}>
            <div style={{ color: 'var(--text-muted)', fontSize: 11, marginBottom: 4 }}>YOUR ACCOUNT</div>
            <div style={{ color: 'var(--text)', fontSize: 13 }}>{currentUser?.email}</div>
            <div style={{ marginTop: 6 }}>
              <span style={s.roleBadge(currentAdminRole)}>{currentAdminRole}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Admins List */}
      <div style={s.card}>
        <div style={s.cardTitle}>
          <span>👥</span>
          All Admin Accounts
          <span style={{ marginLeft: 'auto', background: 'var(--surface2)', borderRadius: 20, padding: '2px 10px', fontSize: 12, color: 'var(--text-secondary)' }}>
            {admins.length} total
          </span>
        </div>

        {loadingAdmins ? (
          <div style={s.emptyState}>Loading admins...</div>
        ) : admins.length === 0 ? (
          <div style={s.emptyState}>No admin accounts found.</div>
        ) : (
          <table style={s.table}>
            <thead>
              <tr>
                <th style={s.th}>Admin</th>
                <th style={s.th}>Role</th>
                <th style={s.th}>Created</th>
                <th style={s.th}>Created By</th>
                {isSuperAdmin && <th style={s.th}>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {admins.map(admin => (
                <tr key={admin.uid}>
                  <td style={s.td}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div style={{ width: 32, height: 32, borderRadius: '50%', background: admin.role === 'superadmin' ? 'rgba(249,115,22,0.15)' : 'rgba(99,102,241,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, color: admin.role === 'superadmin' ? '#F97316' : '#818CF8', flexShrink: 0 }}>
                        {(admin.email || '?').charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <div style={{ color: 'var(--text)', fontSize: 13, fontWeight: 500 }}>
                          {admin.fullName || '—'}
                          {admin.uid === currentUser?.uid && (
                            <span style={{ marginLeft: 6, background: 'var(--surface2)', borderRadius: 4, padding: '1px 6px', fontSize: 10, color: 'var(--text-muted)' }}>YOU</span>
                          )}
                        </div>
                        <div style={{ color: 'var(--text-muted)', fontSize: 12 }}>{admin.email}</div>
                      </div>
                    </div>
                  </td>
                  <td style={s.td}>
                    <span style={s.roleBadge(admin.role)}>{admin.role}</span>
                  </td>
                  <td style={s.td}>
                    {admin.createdAt
                      ? new Date(admin.createdAt.seconds * 1000).toLocaleDateString('en-CA')
                      : '—'}
                  </td>
                  <td style={{ ...s.td, color: 'var(--text-muted)', fontSize: 12 }}>
                    {admin.createdByEmail || 'Firebase Console'}
                  </td>
                  {isSuperAdmin && (
                    <td style={s.td}>
                      {admin.uid === currentUser?.uid ? (
                        <span style={s.selfBadge}>Your account</span>
                      ) : (
                        <button
                          style={s.deleteBtn}
                          onClick={() => handleDeleteClick(admin)}
                          onMouseEnter={e => e.target.style.background = 'rgba(239,68,68,0.08)'}
                          onMouseLeave={e => e.target.style.background = 'transparent'}
                        >
                          🗑️ Remove
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {deleteTarget && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 16, padding: 32, width: '100%', maxWidth: 420 }}>
            <h3 style={{ color: 'var(--text)', margin: '0 0 8px' }}>🗑️ Remove Admin Account</h3>
            <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginBottom: 20, lineHeight: 1.6 }}>
              This will permanently delete <strong style={{ color: 'var(--text)' }}>{deleteTarget.email}</strong>'s admin account and remove their access. This cannot be undone.
            </p>

            <label style={{ display: 'block', color: 'var(--text-secondary)', fontSize: 12, marginBottom: 8 }}>
              Type <strong style={{ color: 'var(--text)' }}>{deleteTarget.email}</strong> to confirm:
            </label>
            <input
              style={{ ...s.input, marginBottom: 16 }}
              placeholder={deleteTarget.email}
              value={deleteConfirm}
              onChange={e => { setDeleteConfirm(e.target.value); setDeleteResult(null); }}
            />

            {deleteResult && (
              <div style={deleteResult.type === 'success' ? s.successBox : s.errorBox}>
                {deleteResult.message}
              </div>
            )}

            <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
              <button
                style={{ flex: 1, background: 'transparent', border: '1px solid var(--border)', borderRadius: 8, padding: '10px', color: 'var(--text-secondary)', fontSize: 14, cursor: 'pointer' }}
                onClick={() => { setDeleteTarget(null); setDeleteConfirm(''); setDeleteResult(null); }}
              >
                Cancel
              </button>
              <button
                style={{ flex: 1, background: deleteConfirm === deleteTarget.email ? '#EF4444' : 'var(--border)', border: 'none', borderRadius: 8, padding: '10px', color: deleteConfirm === deleteTarget.email ? '#fff' : 'var(--text-muted)', fontSize: 14, fontWeight: 600, cursor: deleteConfirm === deleteTarget.email ? 'pointer' : 'not-allowed' }}
                onClick={handleDeleteConfirm}
                disabled={deletingUid || deleteConfirm !== deleteTarget.email}
              >
                {deletingUid ? 'Removing...' : 'Remove Admin'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}